import { NewsArticle } from "schema-dts";
import Head from "next/head";
import gql from "graphql-tag";

import JsonLd from "components/JsonLd";
import {
  ArticleJsonLdDataFragment,
  ArticleJsonLdAuthorFragment,
  ArticleJsonLdImageFragment,
  Maybe,
} from "types/api";

const isImage = (
  x: Maybe<ArticleJsonLdImageFragment>
): x is ArticleJsonLdImageFragment => !!x;

const ArticleJsonLd = ({
  title,
  seoTitle,
  url,
  datePublished,
  dateModified,
  primaryChannel,
  authors,
  shareImage1x1,
  shareImage4x3,
  shareImage16x9,
}: ArticleJsonLdDataFragment) => {
  const images = [shareImage1x1, shareImage4x3, shareImage16x9].filter(isImage);
  return (
    <Head>
      <JsonLd<NewsArticle>
        item={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: seoTitle || title,
          url,
          datePublished: datePublished || undefined,
          dateModified: dateModified || undefined,
          publisher: {
            "@id": "https://www.theatlantic.com/#publisher",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": url,
          },
          ...(images &&
            images.length && {
              image: images.map(({ width, height, url }) => ({
                "@type": "ImageObject",
                width: {
                  "@type": "QuantitativeValue",
                  unitCode: "E37",
                  value: width,
                },
                height: {
                  "@type": "QuantitativeValue",
                  unitCode: "E37",
                  value: height,
                },
                url,
              })),
            }),
          ...(authors && {
            author: authors.map((author: ArticleJsonLdAuthorFragment) => ({
              "@type": "Person",
              name: author.displayName,
              sameAs: author.url,
            })),
          }),
          ...(primaryChannel && { articleSection: primaryChannel.displayName }),
        }}
        key="jsonld-article"
      />
    </Head>
  );
};

ArticleJsonLd.fragment = gql`
  fragment articleJsonLdAuthor on Author {
    url
    displayName
  }

  fragment articleJsonLdImage on BasicImage {
    width
    height
    url
  }

  fragment articleJsonLdData on Article {
    url
    title
    seoTitle
    dateModified
    datePublished
    primaryChannel {
      displayName
    }
    authors {
      ...articleJsonLdAuthor
    }
    shareImage1x1: shareImage(width: 1080, height: 1080) {
      ...articleJsonLdImage
    }
    shareImage16x9: shareImage(width: 1600, height: 900) {
      ...articleJsonLdImage
    }
    shareImage4x3: shareImage(width: 1200, height: 900) {
      ...articleJsonLdImage
    }
  }
`;

export default ArticleJsonLd;
